import type { SharedDriveDeleteMessageDatagramBuf } from "~/classes/generated/message"
import type { SharedDriveDeleteResponseDatagramBuf } from "~/classes/generated/response"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SharedDriveDeleteRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseFile } from "~/utils/flatbuffers/file"
import { addSharedDriveIdToBuf, parseSharedDriveId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("sharedDrive/delete")

export function sendSharedDriveDeleteRequests(requests: SharedDriveDeleteRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const deleteRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const sharedDriveId = addSharedDriveIdToBuf(builder, request.sharedDriveId)

    SharedDriveDeleteRequestDatagramBuf.startSharedDriveDeleteRequestDatagramBuf(builder)
    SharedDriveDeleteRequestDatagramBuf.addRequestId(builder, requestId)
    SharedDriveDeleteRequestDatagramBuf.addOffline(builder, request.offline)
    SharedDriveDeleteRequestDatagramBuf.addSharedDriveId(builder, sharedDriveId)

    const deleteRequest = SharedDriveDeleteRequestDatagramBuf.endSharedDriveDeleteRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addSharedDriveDeleteRequest(builder, deleteRequest)
    deleteRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const del = DatagramBuf.createRequestVector(builder, deleteRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, del)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleSharedDriveDeleteResponseDatagramBuf(datagram: SharedDriveDeleteResponseDatagramBuf) {
  onSharedDriveDeleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    offline: datagram.offline(),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    sharedDriveId: parseSharedDriveId(datagram.sharedDriveId()!),
  })
}

export function onSharedDriveDeleteResponse(response: SharedDriveDeleteResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received sharedDrive delete", clone(response))

  emitInterfaceEvent("sharedDriveDeleteResponse", response, response.requestId)

  if (response.error)
    return onSharedDriveDeleteErrored(response.error)
  if (!response.home || !response.archive)
    throw new Error("No home / archive in delete response")

  const event: SharedDriveEvent = {
    sharedDriveId: response.sharedDriveId,
    home: response.home,
    archive: response.archive,
  }

  emitEntityEvent("sharedDriveDeleted", response.sharedDriveId, event, response.offline)
}

function onSharedDriveDeleteErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "SharedDrive delete request was unsuccessful")
}

export function handleSharedDriveDeleteMessageDatagramBuf(datagram: SharedDriveDeleteMessageDatagramBuf) {
  onSharedDriveDeleteMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    home: parseFile(datagram.home()!),
    archive: parseFile(datagram.archive()!),
  })
}

export function onSharedDriveDeleteMessage(message: SharedDriveDeleteMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  console.debug("Received sharedDrive delete message", clone(message))

  const home = new StaticFile(message.home)
  const sharedDriveId = home.rootSharedDriveId()
  if (!sharedDriveId)
    throw new Error("Shared drive delete message did not contain a shared drive ID")
  const event: SharedDriveEvent = { sharedDriveId, home: message.home, archive: message.archive }

  emitEntityEvent("sharedDriveDeleted", sharedDriveId, event, false)
}
